/**
 * Owns the Leaflet map imperatively. React renders around it; nothing inside the
 * map pane is a React component (spec 01). Subscribes to posStore for the
 * own-ship marker and follow-mode recentering — no React re-render per GPS fix.
 */
import L from 'leaflet';
import { destinationPoint, aisEffectiveHeading, capAisVesselsByDistance, AIS_DIM_AGE_MS, AIS_DROP_AGE_MS } from '@boatnav/core';
import type { AisVessel } from '@boatnav/schemas';
import { posStore, type Fix } from '../nav/posStore.js';
import {
  initialFollowState,
  reduceFollow,
  type FollowMode,
  type FollowState,
} from '../nav/followMachine.js';
import {
  TILE_URL,
  TILE_OPTIONS,
  SKIPPO_URL,
  ENC_URL,
  DEPTH_URL,
  DEPTH_OPTIONS,
  SEAMARK_URL,
  SEAMARK_OPTIONS,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
} from './mapConfig.js';

export type BaseSource = 'kartverket' | 'skippo' | 'enc';

export type FollowListener = (mode: FollowMode) => void;

export class MapController {
  readonly map: L.Map;
  private readonly overlay: L.Renderer;
  private shipMarker: L.Marker | null = null;
  private trackLayer: L.Polyline | null = null;
  private trailLine: L.Polyline | null = null;
  private predictorLine: L.Polyline | null = null;
  private predictorMinutes = 0;
  /** Last known heading, held so the puck keeps pointing when COG drops at rest. */
  private lastCog: number | null = null;
  private depthLayer: L.TileLayer | null = null;
  private seamarkLayer: L.TileLayer | null = null;
  private baseLayer: L.TileLayer;
  private wpMarkers: L.Marker[] = [];
  private routeLine: L.Polyline | null = null;
  private gotoMarker: L.Marker | null = null;
  private gotoLine: L.Polyline | null = null;
  private gotoPoint: { lat: number; lon: number } | null = null;
  private wpHandlers: {
    onDragEnd?: (index: number, lat: number, lon: number) => void;
    onMarkerClick?: (index: number) => void;
  } = {};
  private aisMarkers = new Map<number, L.Marker>(); // keyed by MMSI (spec 16: diff by MMSI, no teardown per poll)
  private aisClickHandler: ((v: AisVessel) => void) | null = null;
  private follow: FollowState;
  private isProgrammatic = false; // reentrancy flag: our pans must not self-cancel
  private followListeners = new Set<FollowListener>();
  private unsubPos: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.map = L.map(container, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: false, // custom 44px buttons in the chrome
      attributionControl: true,
      // Old-GPU mercy: cheaper interactions.
      preferCanvas: true,
      fadeAnimation: false,
    });

    // Seed with the electronic chart (the only user-facing base layer). useMapLayers
    // refreshes attribution and, on the rare deployment with no ENC endpoint,
    // swaps to the Kartverket fallback.
    this.baseLayer = L.tileLayer(ENC_URL, {
      ...TILE_OPTIONS,
      attribution: 'Elektronisk sjøkart © Kartverket',
    }).addTo(this.map);

    // A dedicated pane so the depth overlay sits above the chart tiles but below
    // vector overlays and markers.
    this.map.createPane('depthPane');
    const depthPaneEl = this.map.getPane('depthPane');
    if (depthPaneEl) depthPaneEl.style.zIndex = '250';

    // Single shared canvas renderer for all vector overlays (spec 01).
    this.overlay = L.canvas({ padding: 0.5 });

    this.follow = initialFollowState(this.map.getZoom());

    this.map.on('dragstart', this.onUserGesture);
    this.map.on('zoomend', this.onZoomEnd);

    this.unsubPos = posStore.subscribe((s, prev) => {
      if (s.fix !== prev.fix && s.fix) this.onFix(s.fix);
    });
    const current = posStore.getState().fix;
    if (current) this.onFix(current);

    if (import.meta.env.DEV) {
      (globalThis as unknown as { __losMap?: L.Map }).__losMap = this.map;
    }
  }

  private onUserGesture = (): void => {
    // Programmatic pans set the flag; ignore those so follow doesn't self-cancel.
    if (this.isProgrammatic) return;
    this.setFollow(reduceFollow(this.follow, { type: 'userPan' }));
  };

  private onZoomEnd = (): void => {
    this.setFollow(reduceFollow(this.follow, { type: 'zoomChange', zoom: this.map.getZoom() }));
  };

  private onFix(fix: Fix): void {
    const latlng = L.latLng(fix.lat, fix.lon);
    // Hold the last real heading so the puck doesn't snap north when COG drops
    // to null at rest (positionService nulls COG below ~1 kn).
    if (fix.cogDeg != null) this.lastCog = fix.cogDeg;
    const heading = fix.cogDeg ?? this.lastCog;

    if (!this.shipMarker) {
      this.shipMarker = L.marker(latlng, {
        icon: this.shipIcon(heading),
        interactive: false,
        keyboard: false,
      }).addTo(this.map);
    } else {
      this.shipMarker.setLatLng(latlng);
      this.rotateShip(heading);
    }

    this.redrawPredictor(fix, heading);
    this.redrawGotoLine();

    if (this.follow.mode === 'following') {
      this.isProgrammatic = true;
      this.map.panTo(latlng, { animate: false });
      this.isProgrammatic = false;
    }
  }

  private shipIcon(cogDeg: number | null): L.DivIcon {
    const rot = cogDeg ?? 0;
    return L.divIcon({
      className: 'ownship',
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      html: `<div class="ownship__arrow" style="transform: rotate(${rot}deg)"></div>`,
    });
  }

  private rotateShip(cogDeg: number | null): void {
    const el = this.shipMarker?.getElement()?.querySelector<HTMLElement>('.ownship__arrow');
    if (el) el.style.transform = `rotate(${cogDeg ?? 0}deg)`;
  }

  // --- Follow mode public API (called from React chrome) ---

  toggleFollow(): void {
    if (this.follow.mode === 'following') {
      this.setFollow(reduceFollow(this.follow, { type: 'userPan' })); // treat as manual off
    } else {
      const next = reduceFollow(this.follow, { type: 'followTap', currentZoom: this.map.getZoom() });
      this.setFollow(next);
      const fix = posStore.getState().fix;
      if (fix) {
        this.isProgrammatic = true;
        this.map.setView([fix.lat, fix.lon], next.lastUserZoom, { animate: false });
        this.isProgrammatic = false;
      }
    }
  }

  onFollowChange(listener: FollowListener): () => void {
    this.followListeners.add(listener);
    listener(this.follow.mode);
    return () => this.followListeners.delete(listener);
  }

  private setFollow(next: FollowState): void {
    const changed = next.mode !== this.follow.mode;
    this.follow = next;
    if (changed) this.followListeners.forEach((l) => l(next.mode));
  }

  get overlayRenderer(): L.Renderer {
    return this.overlay;
  }

  /** Draw a recorded track (accent) on the canvas renderer and fit to it. */
  drawTrack(latlngs: Array<[number, number]>): void {
    this.clearTrack();
    if (latlngs.length < 2) return;
    // Canvas can't resolve CSS vars, so read the concrete accent per theme.
    const accent =
      getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#d64a2b';
    this.trackLayer = L.polyline(latlngs, {
      renderer: this.overlay,
      color: accent,
      weight: 3,
      opacity: 0.9,
    }).addTo(this.map);
    this.map.fitBounds(this.trackLayer.getBounds(), { padding: [36, 36] });
  }

  clearTrack(): void {
    if (this.trackLayer) {
      this.trackLayer.remove();
      this.trackLayer = null;
    }
  }

  /**
   * Live breadcrumb behind own-ship (spec 02). A single persistent polyline
   * updated in place — cheaper than remove/re-add each fix. Muted ink so the
   * planned route (accent) stays dominant.
   */
  drawTrail(latlngs: Array<[number, number]>): void {
    if (latlngs.length < 2) {
      this.clearTrail();
      return;
    }
    const ink =
      getComputedStyle(document.documentElement).getPropertyValue('--ink').trim() || '#17323f';
    if (!this.trailLine) {
      this.trailLine = L.polyline(latlngs, {
        renderer: this.overlay,
        color: ink,
        weight: 2.5,
        opacity: 0.55,
        interactive: false,
      }).addTo(this.map);
    } else {
      this.trailLine.setLatLngs(latlngs);
      this.trailLine.setStyle({ color: ink }); // re-read on day/night change
    }
  }

  clearTrail(): void {
    if (this.trailLine) {
      this.trailLine.remove();
      this.trailLine = null;
    }
  }

  /**
   * Course predictor: a dashed accent vector from own-ship along the current
   * heading, its length = distance covered in `predictorMinutes` at SOG — so it
   * grows with speed and shows where you'll be if you hold course (collision cue).
   */
  setPredictorMinutes(minutes: number): void {
    this.predictorMinutes = minutes;
    const fix = posStore.getState().fix;
    if (fix) this.redrawPredictor(fix, fix.cogDeg ?? this.lastCog);
    else this.clearPredictor();
  }

  private redrawPredictor(fix: Fix, heading: number | null): void {
    if (this.predictorMinutes <= 0 || heading == null || fix.sogKn == null || fix.sogKn < 0.5) {
      this.clearPredictor();
      return;
    }
    const distNm = fix.sogKn * (this.predictorMinutes / 60);
    const end = destinationPoint({ lat: fix.lat, lon: fix.lon }, heading, distNm);
    const accent = this.accentColor();
    const pts: Array<[number, number]> = [
      [fix.lat, fix.lon],
      [end.lat, end.lon],
    ];
    if (!this.predictorLine) {
      this.predictorLine = L.polyline(pts, {
        renderer: this.overlay,
        color: accent,
        weight: 2.5,
        opacity: 0.9,
        dashArray: '7 6',
        interactive: false,
      }).addTo(this.map);
    } else {
      this.predictorLine.setLatLngs(pts);
      this.predictorLine.setStyle({ color: accent });
    }
  }

  clearPredictor(): void {
    if (this.predictorLine) {
      this.predictorLine.remove();
      this.predictorLine = null;
    }
  }

  /** Geographic bbox of a centered selection rectangle (fractions of the view). */
  selectionBbox(wFrac: number, hFrac: number): {
    west: number;
    south: number;
    east: number;
    north: number;
  } {
    const size = this.map.getSize();
    const x0 = (size.x * (1 - wFrac)) / 2;
    const y0 = (size.y * (1 - hFrac)) / 2;
    const tl = this.map.containerPointToLatLng([x0, y0]);
    const br = this.map.containerPointToLatLng([size.x - x0, size.y - y0]);
    return {
      west: Math.min(tl.lng, br.lng),
      east: Math.max(tl.lng, br.lng),
      north: Math.max(tl.lat, br.lat),
      south: Math.min(tl.lat, br.lat),
    };
  }

  onViewChange(cb: () => void): () => void {
    this.map.on('moveend', cb);
    this.map.on('zoomend', cb);
    return () => {
      this.map.off('moveend', cb);
      this.map.off('zoomend', cb);
    };
  }

  onMapClick(cb: (lat: number, lon: number) => void): () => void {
    const handler = (e: L.LeafletMouseEvent): void => cb(e.latlng.lat, e.latlng.lng);
    this.map.on('click', handler);
    return () => this.map.off('click', handler);
  }

  /**
   * Long-press "Gå hit" (spec 13): Leaflet re-dispatches the browser's native
   * `contextmenu` event as a map event, which mobile Safari/Chrome fire on
   * touch-and-hold — this is the same mechanism a "long-press to open a
   * context menu" gesture uses generally, not a Leaflet-specific timer.
   * Reliability on the iOS 10 / Chrome 57 floor is unverified in this
   * environment; callers must offer the "Velg i kartet" fallback regardless.
   */
  onLongPress(cb: (lat: number, lon: number) => void): () => void {
    const handler = (e: L.LeafletMouseEvent): void => {
      e.originalEvent?.preventDefault?.();
      cb(e.latlng.lat, e.latlng.lng);
    };
    this.map.on('contextmenu', handler);
    return () => this.map.off('contextmenu', handler);
  }

  /**
   * Go-to pin (spec 13): flag marker + dashed rhumb line own-ship → point,
   * both on the shared canvas renderer. The line's ship-side endpoint tracks
   * `onFix` (via `redrawGotoLine`) so it stays live without a React
   * re-render per GPS tick. `point: null` clears both.
   */
  setGoto(point: { lat: number; lon: number } | null): void {
    this.gotoPoint = point;
    if (!point) {
      this.clearGoto();
      return;
    }
    if (!this.gotoMarker) {
      this.gotoMarker = L.marker([point.lat, point.lon], {
        icon: this.gotoIcon(),
        interactive: false,
        keyboard: false,
      }).addTo(this.map);
    } else {
      this.gotoMarker.setLatLng([point.lat, point.lon]);
    }
    this.redrawGotoLine();
  }

  private gotoIcon(): L.DivIcon {
    const accent = this.accentColor();
    return L.divIcon({
      className: 'goto-flag',
      iconSize: [26, 30],
      iconAnchor: [4, 28],
      html: `<span class="goto-flag__pole"></span><span class="goto-flag__pennant" style="background:${accent}"></span>`,
    });
  }

  private redrawGotoLine(): void {
    if (!this.gotoPoint) return;
    const fix = posStore.getState().fix;
    if (!fix) return; // marker-only until a fix arrives (fully offline for a tapped point)
    const accent = this.accentColor();
    const pts: Array<[number, number]> = [
      [fix.lat, fix.lon],
      [this.gotoPoint.lat, this.gotoPoint.lon],
    ];
    if (!this.gotoLine) {
      this.gotoLine = L.polyline(pts, {
        renderer: this.overlay,
        color: accent,
        weight: 2.5,
        opacity: 0.9,
        dashArray: '4 6',
        interactive: false,
      }).addTo(this.map);
    } else {
      this.gotoLine.setLatLngs(pts);
      this.gotoLine.setStyle({ color: accent });
    }
  }

  clearGoto(): void {
    this.gotoPoint = null;
    if (this.gotoMarker) {
      this.gotoMarker.remove();
      this.gotoMarker = null;
    }
    if (this.gotoLine) {
      this.gotoLine.remove();
      this.gotoLine = null;
    }
  }

  private accentColor(): string {
    return (
      getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#d64a2b'
    );
  }

  private inkColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--ink').trim() || '#17323f';
  }

  private soundingColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--sounding').trim() || '#5b7f8f';
  }

  private measureLine: L.Polyline | null = null;
  private measureMarkers: L.Marker[] = [];

  /**
   * Measure tool (spec 14A): numbered dot markers (DOM, same approach as
   * waypoint markers) + a solid `--ink` polyline on the shared canvas
   * renderer — deliberately not the accent route / dashed predictor / thin
   * trail styling, so it never gets confused with those.
   */
  drawMeasure(points: Array<{ lat: number; lon: number }>): void {
    this.measureMarkers.forEach((m) => m.remove());
    this.measureMarkers = [];
    if (this.measureLine) {
      this.measureLine.remove();
      this.measureLine = null;
    }
    if (points.length === 0) return;

    const ink = this.inkColor();
    if (points.length >= 2) {
      this.measureLine = L.polyline(
        points.map((p) => [p.lat, p.lon]),
        { renderer: this.overlay, color: ink, weight: 2.5, opacity: 0.95 },
      ).addTo(this.map);
    }
    points.forEach((p, i) => {
      const marker = L.marker([p.lat, p.lon], {
        icon: this.measureIcon(i + 1, ink),
        interactive: false,
        keyboard: false,
      }).addTo(this.map);
      this.measureMarkers.push(marker);
    });
  }

  private measureIcon(n: number, ink: string): L.DivIcon {
    return L.divIcon({
      className: 'measure-dot',
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      html: `<span class="measure-dot__circle" style="background:${ink}">${n}</span>`,
    });
  }

  clearMeasure(): void {
    this.drawMeasure([]);
  }

  setWaypointHandlers(handlers: MapController['wpHandlers']): void {
    this.wpHandlers = handlers;
  }

  /**
   * Render numbered, draggable waypoint markers plus the connecting route line.
   * DOM markers only (≤20 waypoints, spec 05); the line uses the canvas renderer.
   */
  renderWaypoints(
    waypoints: Array<{ lat: number; lon: number }>,
    opts: { dashed?: boolean } = {},
  ): void {
    this.wpMarkers.forEach((m) => m.remove());
    this.wpMarkers = [];
    if (this.routeLine) {
      this.routeLine.remove();
      this.routeLine = null;
    }

    const accent = this.accentColor();
    if (waypoints.length >= 2) {
      this.routeLine = L.polyline(
        waypoints.map((w) => [w.lat, w.lon]),
        {
          renderer: this.overlay,
          color: accent,
          weight: 3,
          opacity: 0.9,
          dashArray: opts.dashed ? '8 6' : undefined,
        },
      ).addTo(this.map);
    }

    waypoints.forEach((w, i) => {
      const marker = L.marker([w.lat, w.lon], {
        draggable: true,
        icon: this.wpIcon(i + 1, accent),
        keyboard: false,
      }).addTo(this.map);
      marker.on('dragend', () => {
        const ll = marker.getLatLng();
        this.wpHandlers.onDragEnd?.(i, ll.lat, ll.lng);
      });
      marker.on('click', () => this.wpHandlers.onMarkerClick?.(i));
      this.wpMarkers.push(marker);
    });
  }

  private wpIcon(n: number, accent: string): L.DivIcon {
    return L.divIcon({
      className: 'waypoint',
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      html:
        `<span class="waypoint__halo"></span>` +
        `<span class="waypoint__dot" style="background:${accent}">${n}</span>`,
    });
  }

  clearWaypoints(): void {
    this.renderWaypoints([]);
  }

  private followLayers: L.Layer[] = [];

  /**
   * Follow-route overlay (spec 05): legs behind the active one dimmed, the leg
   * ahead accent-dashed. Non-interactive; own-ship marker stays on top.
   */
  drawFollowRoute(waypoints: Array<{ lat: number; lon: number }>, activeLeg: number): void {
    this.clearFollowRoute();
    if (waypoints.length < 2) return;
    const accent = this.accentColor();
    const dim =
      getComputedStyle(document.documentElement).getPropertyValue('--ink-dim').trim() || '#5c7078';
    const pts = waypoints.map((w) => [w.lat, w.lon] as [number, number]);
    const leg = Math.min(activeLeg, waypoints.length - 1);

    const behind = pts.slice(0, leg);
    const ahead = pts.slice(leg - 1);

    if (behind.length >= 2) {
      this.followLayers.push(
        L.polyline(behind, { renderer: this.overlay, color: dim, weight: 3, opacity: 0.5 }).addTo(
          this.map,
        ),
      );
    }
    this.followLayers.push(
      L.polyline(ahead, {
        renderer: this.overlay,
        color: accent,
        weight: 3,
        opacity: 0.95,
        dashArray: '8 6',
      }).addTo(this.map),
    );
    waypoints.forEach((w) => {
      this.followLayers.push(
        L.circleMarker([w.lat, w.lon], {
          renderer: this.overlay,
          radius: 4,
          color: accent,
          fillColor: accent,
          fillOpacity: 1,
          interactive: false,
        }).addTo(this.map),
      );
    });
  }

  clearFollowRoute(): void {
    this.followLayers.forEach((l) => l.remove());
    this.followLayers = [];
  }

  fitToPoints(points: Array<{ lat: number; lon: number }>): void {
    if (points.length < 1) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon] as [number, number]));
    this.map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
  }

  /** Swap the base chart (Kartverket / Skippo / ENC), crediting the source. */
  setBaseSource(source: BaseSource, attribution: string): void {
    this.baseLayer.remove();
    if (source === 'skippo') {
      this.baseLayer = L.tileLayer(SKIPPO_URL, { ...TILE_OPTIONS, attribution });
    } else if (source === 'enc') {
      this.baseLayer = L.tileLayer(ENC_URL, { ...TILE_OPTIONS, attribution });
    } else {
      this.baseLayer = L.tileLayer(TILE_URL, TILE_OPTIONS);
    }
    this.baseLayer.addTo(this.map);
    this.baseLayer.bringToBack();
  }

  /** Toggle the free OpenSeaMap seamark symbol overlay. */
  setSeamarkOverlay(on: boolean, attribution: string): void {
    if (on && !this.seamarkLayer) {
      this.seamarkLayer = L.tileLayer(SEAMARK_URL, { ...SEAMARK_OPTIONS, attribution }).addTo(
        this.map,
      );
    } else if (!on && this.seamarkLayer) {
      this.seamarkLayer.remove();
      this.seamarkLayer = null;
    }
  }

  /** Toggle the free Kartverket depth-data overlay (contours + soundings). */
  setDepthOverlay(on: boolean): void {
    if (on && !this.depthLayer) {
      this.depthLayer = L.tileLayer(DEPTH_URL, DEPTH_OPTIONS).addTo(this.map);
    } else if (!on && this.depthLayer) {
      this.depthLayer.remove();
      this.depthLayer = null;
    }
  }

  /** `null` clears the handler (spec 16 vessel tap → sheet). */
  setAisVesselClickHandler(handler: ((v: AisVessel) => void) | null): void {
    this.aisClickHandler = handler;
  }

  /**
   * Renders the AIS vessel layer (spec 16), diffed by MMSI: existing markers
   * are moved/rotated/restyled in place — no remove/re-add, no teardown per
   * poll — and only the add/remove delta touches the map. Drops targets
   * older than 10 min client-side (defensive; the server already trims
   * these) and caps at `AIS_RENDER_CAP` nearest the view center (old-GPU
   * constraint), returning enough for the caller to show a "N fartøy —
   * zoom inn" chip when capped.
   */
  setAisVessels(vessels: AisVessel[]): { shown: number; total: number; capped: boolean } {
    const now = Date.now();
    const fresh = vessels.filter((v) => now - v.ts <= AIS_DROP_AGE_MS);
    const center = this.map.getCenter();
    const { shown, capped } = capAisVesselsByDistance(fresh, { lat: center.lat, lon: center.lng });

    const sounding = this.soundingColor();
    const ink = this.inkColor();
    const seen = new Set<number>();

    for (const v of shown) {
      seen.add(v.mmsi);
      const heading = aisEffectiveHeading(v.headingDeg, v.cogDeg);
      const dim = now - v.ts > AIS_DIM_AGE_MS;

      let marker = this.aisMarkers.get(v.mmsi);
      if (!marker) {
        marker = L.marker([v.lat, v.lon], {
          icon: this.aisIcon(heading, sounding, ink, dim),
          keyboard: false,
        }).addTo(this.map);
        this.aisMarkers.set(v.mmsi, marker);
      } else {
        marker.setLatLng([v.lat, v.lon]);
        this.updateAisIcon(marker, heading, sounding, ink, dim);
      }
      // Re-bind on every call (cheap) so the closure always captures this
      // poll's vessel data (SOG/COG/ts change even when the marker doesn't).
      marker.off('click');
      marker.on('click', () => this.aisClickHandler?.(v));
    }

    for (const [mmsi, marker] of this.aisMarkers) {
      if (!seen.has(mmsi)) {
        marker.remove();
        this.aisMarkers.delete(mmsi);
      }
    }

    return { shown: shown.length, total: fresh.length, capped };
  }

  clearAisVessels(): void {
    this.aisMarkers.forEach((m) => m.remove());
    this.aisMarkers.clear();
  }

  /** Currently-rendered AIS marker count (tests + the capped-chip caller). */
  getAisMarkerCount(): number {
    return this.aisMarkers.size;
  }

  /** The rendered marker's DOM node for a given MMSI, or `null` if not
   *  currently shown — used to assert marker identity is reused across
   *  polls rather than recreated (spec 16 acceptance). */
  getAisMarkerElement(mmsi: number): HTMLElement | null {
    return this.aisMarkers.get(mmsi)?.getElement() ?? null;
  }

  private aisIcon(headingDeg: number, sounding: string, ink: string, dim: boolean): L.DivIcon {
    return L.divIcon({
      className: `ais-vessel${dim ? ' ais-vessel--dim' : ''}`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
      html:
        `<div class="ais-vessel__arrow" style="transform: rotate(${headingDeg}deg)">` +
        `<svg viewBox="0 0 18 18" width="18" height="18">` +
        `<path d="M9 1 L15 16 L9 12.5 L3 16 Z" fill="${sounding}" stroke="${ink}" stroke-width="1"/>` +
        `</svg></div>`,
    });
  }

  /** In-place icon update (no `setIcon()`/DOM teardown) — keeps the same
   *  Leaflet marker + DOM node across polls, only moving/rotating/restyling
   *  it (spec 16 acceptance: assert marker identity is reused). */
  private updateAisIcon(marker: L.Marker, headingDeg: number, sounding: string, ink: string, dim: boolean): void {
    const el = marker.getElement();
    if (!el) return;
    el.classList.toggle('ais-vessel--dim', dim);
    const arrow = el.querySelector<HTMLElement>('.ais-vessel__arrow');
    if (arrow) arrow.style.transform = `rotate(${headingDeg}deg)`;
    const path = el.querySelector<SVGPathElement>('path');
    if (path) {
      path.setAttribute('fill', sounding);
      path.setAttribute('stroke', ink);
    }
  }

  zoomIn(): void {
    this.map.zoomIn();
  }

  zoomOut(): void {
    this.map.zoomOut();
  }

  destroy(): void {
    this.unsubPos?.();
    this.map.off('dragstart', this.onUserGesture);
    this.map.off('zoomend', this.onZoomEnd);
    this.map.remove();
  }
}
